import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import isFunction from 'lodash/isFunction';
import kebabCase from 'lodash/kebabCase';
import snakeCase from 'lodash/snakeCase';

import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import ActionButton from 'components/buttons/action-button/ActionButton';
import { withModal } from 'components/modal/ModalHOC';

import CampaignPurchaseOrderContext from 'modules/campaigns/components/purchase-order/context/CampaignPurchaseOrderContext';
import CampaignPurchaseOrderItemModal from '../modals/item/CampaignPurchaseOrderItemModal';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { __t, applyTranslations } from 'utils/language';

import './campaign-purchase-order-item.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./campaign-purchase-order-item.i18n.json'));

import type { State } from 'types';
import type {
  CampaignPurchaseOrderItem as CampaignPurchaseOrderItemAlias,
  CampaignPurchaseOrderItemIdentifier
} from '../../types';

interface Props {
  identifier: CampaignPurchaseOrderItemIdentifier
}

const ActionButtonWithModal = withModal(ActionButton, {
  modalContent: CampaignPurchaseOrderItemModal,
  containerClass: 'campaign-purchase-order-item__modal'
});

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'campaign_purchase_order_item';

const CampaignPurchaseOrderItem: React.FC<Props> = ({
  identifier
}): JSX.Element => {
  const dispatch = useDispatch();
  const context = React.useContext(CampaignPurchaseOrderContext);
  const result = useSelector((state: State) => state.campaignPurchaseOrder.result);
  const item = context?.purchaseOrder[identifier] ?? {} as CampaignPurchaseOrderItemAlias;

  const handleDelete = (): void => {
    dispatch(showConfirmModal({
      title: __t(`${MSG_KEY}.modals.confirmation.title`),
      message: __t(`${MSG_KEY}.modals.confirmation.message`, {
        mode: __t(`${MSG_KEY}.labels.${snakeCase(identifier)}`)
      }),
      confirmButtonLabel: __t(`${MSG_KEY}.modals.confirmation.buttons.confirm`),
      cancelButtonLabel: __t(`${COMMON_MSG_KEY}.action_button.cancel`),
      onClose: () => dispatch(hideConfirmModal()),
      onCancel: () => dispatch(hideConfirmModal()),
      onConfirm: () => {
        if (isFunction(context?.onDelete)) {
          context?.onDelete(identifier);
        }

        dispatch(hideConfirmModal());
      },
      modalOpenerClassName: 'campaign-purchase-order-item__modal'
    }));
  };

  const handleCheckboxChange = (
    event: React.SyntheticEvent<HTMLInputElement>
  ): void => {
    const target = event.currentTarget;
    const isChecked = target.checked;

    if (isFunction(context?.onCheckboxChange)) {
      context?.onCheckboxChange(identifier, isChecked);
    }
  };

  const renderAddButton = (): React.ReactNode => (
    <div className="campaign-purchase-order-item__button">
      <ActionButtonWithModal
        className="campaign-purchase-order-item__modal-trigger"
        color="secondary"
        disabled={!item.isChecked}
        modalContentProps={{
          identifier
        }}
      >
        {__t(`${MSG_KEY}.buttons.${snakeCase(item.identifier)}`)}
      </ActionButtonWithModal>
    </div>
  );

  const renderEditButtons = (): React.ReactNode => {
    const buttonSize = {
      width: 60,
      height: 24
    };

    return (
      <React.Fragment>
        <div className="campaign-purchase-order-item__button">
          <ActionButtonWithModal
            color="tertiary"
            style="thin"
            width="minimum"
            customSize={buttonSize}
            modalContentProps={{
              identifier,
              isEditMode: item.isSaved
            }}
          >
            {__t(`${COMMON_MSG_KEY}.action_button.edit`)}
          </ActionButtonWithModal>
        </div>
        <div className="campaign-purchase-order-item__button">
          <ActionButton
            color="tertiary"
            style="thin"
            width="minimum"
            customSize={buttonSize}
            onClick={handleDelete}
          >
            {__t(`${COMMON_MSG_KEY}.action_button.delete`)}
          </ActionButton>
        </div>
      </React.Fragment>
    );
  };

  const renderCheckbox = (): React.ReactNode => (
    <div className="campaign-purchase-order-item__checkbox">
      <InputCheckbox
        key={`campaign-purchase-order-item__checkbox-${item.identifier}`}
        id={`campaign-purchase-order-item__checkbox-${item.identifier}`}
        label={__t(`${MSG_KEY}.checkboxes.${snakeCase(item.identifier)}`)}
        tooltipMessage={__t(`${MSG_KEY}.checkboxes.tooltips.${snakeCase(item.identifier)}`)}
        tooltipClassNameWrapper="campaign-purchase-order-item__tooltip-wrapper"
        tooltipClassName="campaign-purchase-order-item__tooltip"
        checked={item.isChecked}
        disabled={item.isSaved} // force user to delete value first
        onChange={handleCheckboxChange}
      />
    </div>
  );

  const renderCreateMode = (): React.ReactNode => (
    <div className="campaign-purchase-order-item__mode">
      {renderAddButton()}
    </div>
  );

  const renderEditMode = (): React.ReactNode => {
    // use value direct from BE (instead of local context)
    // this prevents value from being updated while editing in modal
    const value = identifier === 'pon'
      ? result?.pon
      : result?.costCenter;

    const labelClasses = classnames('campaign-purchase-order-item__edit-label', {
      [`campaign-purchase-order-item__edit-label--type-${kebabCase(identifier)}`]: true
    });

    return (
      <div className="campaign-purchase-order-item__mode">
        <div className={labelClasses}>
          {__t(`${MSG_KEY}.labels.${snakeCase(identifier)}`)}:
        </div>
        <div
          className="campaign-purchase-order-item__edit-value text-overflow-ellipsis"
          title={value ?? ''}
        >
          {value}
        </div>
        <div className="campaign-purchase-order-item__edit-buttons">
          {renderEditButtons()}
        </div>
      </div>
    );
  };

  const containerClasses = classnames('campaign-purchase-order-item', {
    [`campaign-purchase-order-item--type-${kebabCase(identifier)}`]: true
  });

  return (
    <div className={containerClasses}>
      <div className="campaign-purchase-order-item__section">
        {renderCheckbox()}
        {
          item.isSaved
            ? renderEditMode()
            : renderCreateMode()
        }
      </div>
    </div>
  );
};

export default CampaignPurchaseOrderItem;
